import axiosInstance from '../lib/axios';
import type {
  CaseSearchParams,
  CaseSearchResult,
  HashtagSearchResult,
  PaginationMeta,
} from '../types/search.types';

interface BackendPaginated<T> {
  items: T[];
  pagination: PaginationMeta;
}

interface MasterOption {
  value: string | number;
  label: string;
  code?: string;
}

export const searchService = {
  searchCases: async (
    params: CaseSearchParams
  ): Promise<{ data: CaseSearchResult[]; pagination: PaginationMeta }> => {
    const { hashtags, ...rest } = params;
    const queryParams: Record<string, unknown> = { ...rest };
    if (hashtags?.trim()) {
      queryParams.hashtags = hashtags
        .split(',')
        .map((t) => t.trim().replace(/^#/, ''))
        .filter(Boolean);
    }
    const res = await axiosInstance.get<{ success: boolean; data: BackendPaginated<CaseSearchResult> }>(
      '/cases',
      { params: queryParams }
    );
    const backendData = res.data?.data;
    const items = (backendData?.items ?? []).map((row: any) => ({
      ...row,
      document_count: Array.isArray(row.documents) ? row.documents.length : 0,
    }));
    return {
      data: items,
      pagination: backendData?.pagination ?? {
        page: 1,
        limit: 10,
        total: 0,
        totalPages: 0,
      },
    };
  },

  searchByHashtag: async (
    tags: string[],
    page = 1,
    limit = 10
  ): Promise<{ data: HashtagSearchResult[]; pagination: PaginationMeta }> => {
    const tagsParam = tags.map((t) => t.replace(/^#/, '').trim()).filter(Boolean).join(',');
    const res = await axiosInstance.get<{ success: boolean; data: BackendPaginated<any> }>(
      '/documents/search-by-hashtag',
      { params: { tags: tagsParam, page, limit } }
    );
    const backendData = res.data?.data;
    const items = (backendData?.items ?? []).map((row: any) => ({
      document_id: row.document_id,
      original_file_name: row.original_file_name,
      file_name: row.original_file_name ?? row.file_name ?? '—',
      file_extension: row.file_extension ?? null,
      mime_type: row.mime_type ?? null,
      folder_type: row.folder_type ?? '—',
      document_type: row.document_type ?? '—',
      classification: row.classification ?? 'Public',
      approval_status: row.approval_status ?? '—',
      hashtags: Array.isArray(row.hashtags) ? row.hashtags : [],
      uploaded_at: row.created_at ?? row.uploaded_at ?? '',
      uploaded_by_name: row.uploaded_by_name ?? '—',
      can_view: typeof row.can_view === 'boolean' ? row.can_view : undefined,
      page_count: row.page_count ?? null,
      file_size_bytes: row.file_size_bytes ?? null,
      case_id: row.case_id,
      cms_number: row.case?.cms_number ?? '—',
      rrms_ref_number: row.case?.rrms_ref_number ?? '—',
      ps_name: row.case?.ps_name ?? '—',
      case: row.case,
    }));
    return {
      data: items,
      pagination: backendData?.pagination ?? {
        page: 1,
        limit: 10,
        total: 0,
        totalPages: 0,
      },
    };
  },

  /** Documents in cases matching OCR document-content (rrms_cases.ocr_hashtags). */
  searchByOcrHashtag: async (
    tags: string[],
    page = 1,
    limit = 10
  ): Promise<{ data: HashtagSearchResult[]; pagination: PaginationMeta }> => {
    const tagsParam = tags.map((t) => t.replace(/^#/, '').trim()).filter(Boolean).join(',');
    const res = await axiosInstance.get<{ success: boolean; data: BackendPaginated<any> }>(
      '/documents/search-by-ocr-hashtag',
      { params: { tags: tagsParam, page, limit } }
    );
    const body = res.data as { success?: boolean; message?: string; data?: BackendPaginated<any> };
    if (body?.success === false) {
      throw new Error(body.message || 'OCR hashtag search failed');
    }
    const backendData = body?.data;
    const items = (backendData?.items ?? []).map((row: any) => ({
      document_id: row.document_id,
      original_file_name: row.original_file_name,
      file_name: row.original_file_name ?? row.file_name ?? '—',
      file_extension: row.file_extension ?? null,
      mime_type: row.mime_type ?? null,
      folder_type: row.folder_type ?? '—',
      document_type: row.document_type ?? '—',
      classification: row.classification ?? 'Public',
      approval_status: row.approval_status ?? '—',
      hashtags: Array.isArray(row.hashtags) ? row.hashtags : [],
      ocr_hashtags: Array.isArray(row.case?.ocr_hashtags) ? row.case.ocr_hashtags : [],
      uploaded_at: row.created_at ?? row.uploaded_at ?? '',
      uploaded_by_name: row.uploaded_by_name ?? '—',
      can_view: typeof row.can_view === 'boolean' ? row.can_view : undefined,
      page_count: row.page_count ?? null,
      file_size_bytes: row.file_size_bytes ?? null,
      case_id: row.case_id,
      cms_number: row.case?.cms_number ?? '—',
      rrms_ref_number: row.case?.rrms_ref_number ?? '—',
      ps_name: row.case?.ps_name ?? '—',
      case: row.case,
    }));
    return {
      data: items,
      pagination: backendData?.pagination ?? {
        page: 1,
        limit: 10,
        total: 0,
        totalPages: 0,
      },
    };
  },

  getDistricts: async (): Promise<MasterOption[]> => {
    const res = await axiosInstance.get<{ success: boolean; data: any[] }>('/masters/districts');
    const data = res.data?.data;
    if (!Array.isArray(data)) return [];
    return data.map(d => ({ value: d.id ?? d.value, label: d.name ?? d.label ?? String(d.value), code: d.code }));
  },

  getPoliceStations: async (district?: string): Promise<MasterOption[]> => {
    const res = await axiosInstance.get<{ success: boolean; data: any[] }>(
      '/masters/police-stations',
      { params: district ? { district } : {} }
    );
    const data = res.data?.data;
    if (!Array.isArray(data)) return [];
    return data.map(d => ({ value: d.id ?? d.value, label: d.name ?? d.label ?? String(d.value), code: d.code }));
  },

  getCaseTypes: async (): Promise<MasterOption[]> => {
    const res = await axiosInstance.get<{ success: boolean; data: any[] }>('/masters/case-types');
    const data = res.data?.data;
    if (!Array.isArray(data)) return [];
    return data.map(d => ({ value: d.code ?? d.id ?? d.value, label: d.name ?? d.label ?? String(d.value), code: d.code }));
  },

  getCaseStatuses: async (): Promise<MasterOption[]> => {
    const res = await axiosInstance.get<{ success: boolean; data: any[] }>('/masters/case-statuses');
    const data = res.data?.data;
    if (!Array.isArray(data)) return [];
    return data.map(d => ({ value: d.code ?? d.id ?? d.value, label: d.name ?? d.label ?? String(d.value), code: d.code }));
  },

  getFolderTypes: async (): Promise<MasterOption[]> => {
    const res = await axiosInstance.get<{ success: boolean; data: any[] }>('/masters/folder-types');
    const data = res.data?.data;
    if (!Array.isArray(data)) return [];
    return data.map(d => ({ value: d.id ?? d.value, label: d.name ?? d.label ?? String(d.value), code: d.code }));
  },

  getDocumentTypes: async (folderType?: string): Promise<MasterOption[]> => {
    const res = await axiosInstance.get<{ success: boolean; data: any[] }>(
      '/masters/document-types',
      { params: folderType ? { folder_type: folderType } : {} }
    );
    const data = res.data?.data;
    if (!Array.isArray(data)) return [];
    return data.map(d => ({ value: d.id ?? d.value, label: d.name ?? d.label ?? String(d.value), code: d.code }));
  },
};

export default searchService;
